import React, { useCallback, useEffect, useRef, useState } from 'react'

import * as yup from 'yup'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router'
import { FormikProvider, useFormik } from 'formik'

import SelectDoctorAndHospital from './SelectDoctorAndHospital'
import SelectSurgicalOrderPatient from './SelectPatient'
import ReviewPatient from './ReviewPatient'
import SelectSurgeryInfo from './SelectSurgeryInfo'
import SurgicalOrderReview from './SurgicalOrderReview'
import { Hospital } from 'domain/entities/hospital-model'
import { Doctor } from 'domain/entities/doctor-model'
import { Patient, PatientHealthInsurance } from 'domain/entities/patient-model'
import { LoadPatientByCpf } from 'domain/usecases/patient/load-patient-by-cpf'
import { Tuss } from 'domain/entities/tuss'
import { Cid } from 'domain/entities/cid'
import { InvitePatient } from 'domain/usecases/patient/invite-patient'
import { dateInputFormat } from 'presentation/utils/date-format'
import { RegisterSurgicalOrder } from 'domain/usecases/surgical-order/register-surgical-order'
import Modal from 'presentation/shared/components/Modal'
import { AddFavorite } from 'domain/usecases/surgical-order/add-favorite'
import { SurgeryCenter } from 'domain/entities/surgery-center-model'
import { useAddSurgicalOrderFunctions } from 'presentation/hospital/pages/AddSurgicalOrder/load-surgical-order'
import ConditionalCarousel, {
  CarouselState
} from 'presentation/shared/components/Carousel/ConditionalCarousel'
import { useStores } from 'presentation/hooks/use-stores'
import { AddPatient } from 'domain/usecases/patient/add-patient'
import { UploadPatientDocument } from 'domain/usecases/patient/upload-patient-document'
import { UploadSurgicalOrderDocument } from 'domain/usecases/surgical-order/upload-surgical-order-document'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import {
  HealthInsurancePlans,
  Plans,
  SubPlan
} from 'domain/usecases/health-insurance/load-health-insurance-plans'
import { SearchPatientInGateway } from 'domain/usecases/patient/search-patient-in-gateway'
import { useLocation } from 'react-router-dom'
import normalizeText from 'common/utils/getNormalizedText'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useServices } from 'presentation/hooks/use-services'
import { generateHash } from 'common/utils/generateHash'
import { renameFile } from 'common/utils/file/renameFile'
import { PatientDocument } from 'common/enum/patient-document'
import { getThumbnail } from 'presentation/shared/components/UploadSection/Functions'
import { DocumentItem } from 'presentation/shared/components/DocumentsArea/DocumentsAreaModal'
import { DocumentsType } from 'domain/entities/document-types'
import { Profile } from 'common/enum/profile'
import { Document } from 'domain/entities/document'
import {
  useCrmoDocumentUpload,
  CrmoFileToUpload
} from 'presentation/hooks/use-crmo-document-upload'
import { GetPresignedUrlCrmo } from 'domain/usecases/surgical-order/get-presigned-url-crmo'
import { UploadFileToS3 } from 'domain/usecases/surgical-order/upload-file-to-s3'
import { ProcessDocumentCrmo } from 'domain/usecases/surgical-order/process-document-crmo'

type Props = {
  loadPatient?: LoadPatientByCpf
  doctors: Doctor[]
  hospitals: Hospital[]
  cids: Cid[]
  tussList: Tuss[]
  insurances: HealthInsurancePlans[]
  plans: Plans[]
  subPlans: SubPlan[]
  invitePatient: InvitePatient
  addOrder: RegisterSurgicalOrder
  addFavorite?: AddFavorite
  surgeryCenter: SurgeryCenter[]
  addPatient: AddPatient
  uploadDocument?: UploadPatientDocument
  searchPatient: SearchPatientInGateway
  uploadSurgicalOrderDocument?: UploadSurgicalOrderDocument
} & WithLoadingProps

// const MINIMAL_DOCUMENTS_ATTACHED = 9

export const SecretaryAddSurgicalOrderForm = WithLoading(
  ({
    hospitals,
    doctors,
    insurances,
    cids,
    tussList,
    addOrder,
    addFavorite,
    surgeryCenter,
    plans,
    addPatient,
    uploadDocument,
    setIsLoading,
    subPlans,
    searchPatient,
    invitePatient
  }: Props) => {
    const [carousel, setCarousel] = useState({} as CarouselState)
    const [handleSuccessModal, setHandleSuccessModal] = useState(false)
    const [handleNotFoundPatientModal, setHandleNotFoundPatientModal] =
      useState(false)
    const [handleFavoriteModal, setHandleFavoriteModal] = useState(false)
    const [createPatientMode, setCreatePatientMode] = useState(false)
    const history = useHistory()
    const context = useAddSurgicalOrderFunctions()
    const user = useStores()?.currentAccount?.getCurrentAccount()?.user
    const location =
      useLocation<{ patient?: Patient; old_solicitation?: number }>()

    const initialValues = {
      patient: location.state?.patient
    } as AddSurgicalOrderFormValues

    const services = useServices()
    const surgicalOrderService = services.surgicalOrder
    const patientService = services.patient

    const groupIdRef = useRef<string>(generateHash())

    const getPresignedUrlCrmo = useCallback(
      (params: GetPresignedUrlCrmo.Params) =>
        surgicalOrderService.getPresignedUrlCrmo(params),
      [surgicalOrderService]
    )
    const uploadFileToS3 = useCallback(
      (params: UploadFileToS3.Params) =>
        surgicalOrderService.uploadFileToS3(params),
      [surgicalOrderService]
    )
    const processDocumentCrmo = useCallback(
      (params: ProcessDocumentCrmo.Params) =>
        surgicalOrderService.processDocumentCrmo(params),
      [surgicalOrderService]
    )

    const { isUploading, uploadFiles } = useCrmoDocumentUpload({
      groupId: groupIdRef.current,
      createdBy: user?.user_id || 0,
      createdByName: user?.name || '',
      getPresignedUrl: getPresignedUrlCrmo,
      uploadFileToS3,
      processDocument: processDocumentCrmo
    })

    const getDocuments = async (surgical_order_id: number) => {
      const surgicalOrder = await surgicalOrderService.loadSurgicalOrder(
        surgical_order_id,
        ['documents {', '  document_id', '  type', '  name', '  group_id', '}']
      )
      const documents = surgicalOrder?.documents || []
      const documents_attached: DocumentItem[] = []
      await Promise.all(
        documents.map(async ({ group_id, type, name, document_id }) => {
          if (!group_id) return
          const response = await surgicalOrderService.loadSurgicalOrderDocument(
            {
              surgical_order_id: group_id,
              document_id
            }
          )
          const file = new File([response.data], name, {
            type: response.contentType
          })
          const label = DocumentsType.getDocumentTypeByType(
            type as PatientDocument
          )?.label
          documents_attached.push({
            file,
            type,
            document_id,
            label,
            thumbnail: await getThumbnail(file)
          })
        })
      )
      formHandler.setFieldValue('documents_attached', documents_attached)
    }

    useEffect(() => {
      if (
        location?.state?.old_solicitation &&
        formHandler.values.documents_attached?.length === 0
      ) {
        getDocuments(location.state.old_solicitation)
      }
    }, [location?.state?.old_solicitation])

    const formHandler = useFormik({
      initialValues: {
        ...initialValues,
        procedures: [],
        cids: [],
        opmes: [],
        documents_attached: [],
        requestedDailies: 0,
        healthInsurance: {},
        // cooperative_name: '',
        patientHospitalized:
          initialValues.patient?.internation?.paciente_internado ?? false
      },
      validationSchema:
        user?.role === Profile.CRMO ? opmeSchema : secretarySchema,
      onSubmit: async (values) => {
        setIsLoading(true)
        const allergies: string[] = []

        if (values.allergy === 'Látex') {
          allergies.push('Látex')
        } else if (values.allergy === 'latex-not-informed') {
          allergies.push('latex-not-informed')
        }
        if (values.otherAllergies) {
          allergies.push(values.otherAllergies)
        }

        const formatedCid = values.cids
          .filter((item) => item)
          .map((item: any) => ({
            code: item.cid_id,
            description: item.description
          }))

        const reqObject: RegisterSurgicalOrder.Params = {
          patient_id: values.patient.patient_id,
          hospital_id: values.hospital.hospital_id,
          doctor_id: Number(values.doctor.doctor_id),
          region: values.region,
          freeze: values.freeze,
          hospitalizationMode: values.hospitalizationMode,
          hospitalizationType: values.hospitalizationType,
          clinicalHistory: values.clinicalHistory,
          cti: values.cti,
          extraBlood: values.extraBlood,
          technique: values.technique,
          allergy: allergies,
          procedure: formatProcedures(values.procedures),
          cid: formatedCid,
          surgical_center_id: Number(values.surgery_center.codeCenter),
          observation: values.observations,
          surgicalCenter: values.surgery_center.description,
          patientName: normalizeText(values.patient.name),
          doctorName: normalizeText(values.doctor.name),
          opme: {
            solicitations: formatOpme(values.opmes),
            providers: values.providers?.filter((val) => !isEmpty(val)) || []
          },
          healthInsurance: values.healthInsurance,
          patientHospitalized: values.patientHospitalized,
          cooperative_name: values.cooperative_name
        }

        delete reqObject.healthInsurance.healthSubPlanCode
        delete reqObject.healthInsurance.healthSubPlanName

        if (location.state?.old_solicitation) {
          reqObject.old_solicitation = location.state?.old_solicitation
        }

        if (values.expectedDate) {
          reqObject.expectedDate = moment(
            values.expectedDate,
            'YYYY-MM-DDTHH:mm'
          ).toISOString()
        }

        try {
          const group_id = groupIdRef.current
          reqObject.requestedDailies = Number(values.requestedDailies)

          // Upload dos documentos acontece aqui, no submit final ("Criar
          // Planejamento"), enviando apenas os arquivos listados no modal neste
          // momento. Uma falha parcial de upload apenas exibe um toast e não
          // impede a criação do pedido.
          await handleUploadDocuments(values.documents_attached)

          const newSurgicalOrder = await addOrder.add(reqObject, Profile.CRMO)
          if (newSurgicalOrder.surgical_order_id) {
            await surgicalOrderService.mergeDocumentsCrmo({
              group_id,
              surgical_order_id: Number(newSurgicalOrder.surgical_order_id)
            })
          }

          setHandleSuccessModal(true)
        } catch (error: any) {
          toast.error(error.message)
        }
        setIsLoading(false)
      }
    })

    const findPatient = async (cpf: string) => {
      try {
        setIsLoading(true)
        const patients = await patientService.getPatientByCpfOrName({ cpf })
        if (patients && patients.length > 0) {
          const gettedPatient: any = patients[0]
          const normalizedPatient = {
            ...gettedPatient,
            cpf: gettedPatient.cpf ?? gettedPatient.user?.cpf,
            email: gettedPatient.email ?? gettedPatient.user?.email,
            phone: gettedPatient.phone ?? gettedPatient.user?.phone,
            birthday: dateInputFormat(
              gettedPatient.birthday ??
                gettedPatient.birth_date ??
                gettedPatient.birthDate
            ),
            maritalStatus:
              gettedPatient.maritalStatus ?? gettedPatient.marital_status,
            landlinePhone: gettedPatient.landlinePhone ?? gettedPatient.landline
          }
          await formHandler.setFieldValue(
            'patient',
            normalizedPatient as Patient
          )
          const isPatientHospitalized =
            normalizedPatient.internation?.paciente_internado ?? false
          await formHandler.setFieldValue(
            'patientHospitalized',
            isPatientHospitalized
          )

          if (normalizedPatient.healthInsurance?.healthInsuranceCode) {
            context?.loadHealthPlans(
              normalizedPatient.healthInsurance.healthInsuranceCode
            )
          }
        } else {
          setHandleNotFoundPatientModal(true)
          return toast.error('Paciente não encontrado')
        }
      } catch (error: any) {
        setHandleNotFoundPatientModal(true)
        return toast.error('Paciente não encontrado')
      } finally {
        setIsLoading(false)
      }
    }

    const submitFavorite = async (value: string) => {
      const providers: string[] =
        formHandler.values?.providers &&
        formHandler.values?.providers.filter((val) => !isEmpty(val))

      const formatedCid = formHandler.values.cids
        .filter((item) => item)
        .map((item: { cid_id: string; description: string }) => ({
          code: item.cid_id,
          description: item.description
        }))

      try {
        await addFavorite?.add({
          name: value,
          cid: formatedCid,
          procedure: formatProcedures(formHandler.values.procedures),
          opme: {
            solicitations: formatOpme(formHandler.values.opmes),
            providers: providers ?? []
          }
        })
        setHandleFavoriteModal(true)
      } catch (e: any) {
        toast.error(e.message)
      }
    }

    function slideNext() {
      carousel.slideNext()
      window.scrollTo(0, 0)
    }

    const handleUploadDocuments = async (
      documentsAttached: DocumentItem[]
    ): Promise<void> => {
      const filesToUpload: CrmoFileToUpload[] = documentsAttached
        .filter((file) => file.file && file.file.size > 0 && !file.document_id)
        .map((file) => ({
          file: renameFile(file.file, file.label as string, {
            forceOriginalExtension: true
          }),
          documentName: file.file.name,
          documentType: file.label as string
        }))

      if (filesToUpload.length === 0) {
        return
      }

      const { hasError } = await uploadFiles(filesToUpload)
      if (hasError) {
        toast.error('Falha no upload de um ou mais arquivos.')
      } else {
        toast.success('Documentos enviados com sucesso.')
      }
    }

    const handelClosePacientModal = () => setHandleNotFoundPatientModal(false)

    return (
      <FormikProvider value={formHandler}>
        <ConditionalCarousel setState={setCarousel}>
          <SelectDoctorAndHospital
            hospitals={hospitals}
            doctors={doctors}
            goNext={slideNext}
            goBack={history.goBack}
            surgeryCenters={surgeryCenter}
          />

          <SelectSurgicalOrderPatient
            goNext={slideNext}
            handleNotFoundPatientModal={handleNotFoundPatientModal}
            handleClosePacientModal={handelClosePacientModal}
            findPatient={findPatient}
            goBack={carousel.slidePrev}
            setCreatePatientMode={setCreatePatientMode}
            searchPatient={searchPatient}
          />

          <ReviewPatient
            insurances={insurances}
            profile={user?.role}
            goNext={slideNext}
            goBack={carousel.slidePrev}
            findPatient={findPatient}
            plans={plans}
            createPatientMode={createPatientMode}
            addPatient={addPatient}
            invitePatient={invitePatient}
            uploadDocument={uploadDocument}
            setCreatePatientMode={setCreatePatientMode}
            subPlans={subPlans}
          />

          <SelectSurgeryInfo
            submit={slideNext}
            cidList={cids}
            tussList={tussList}
            goBack={carousel.slidePrev}
            addFavorite={(value) => submitFavorite(value)}
            profile={user?.role}
            defaultOpme={formHandler.values.use_opme}
          />

          <SurgicalOrderReview
            goBack={carousel.slidePrev}
            profile={user?.role}
            isUploading={isUploading}
          />
        </ConditionalCarousel>

        <Modal
          close={() => history.push('/home')}
          show={handleSuccessModal}
          title="Planejamento cirúrgico criado com sucesso!"
        />
        <Modal
          close={() => setHandleFavoriteModal(false)}
          show={handleFavoriteModal}
          title="Procedimento favoritado com sucesso!"
        />
      </FormikProvider>
    )
  }
)

export default SecretaryAddSurgicalOrderForm

export type AddSurgicalOrderFormValues = {
  room: string
  technique: string
  laterality: string
  freeze?: boolean
  cti?: boolean
  extraBlood: boolean
  allergy: string
  hospitalizationMode: string
  hospitalizationType: string
  otherAllergies: string
  clinicalHistory: string
  attendanceCode: string
  providers: string[]
  height: string
  weight: string
  region: string
  front: File
  back: File
  isHospitalized: string
  requestedDailies: string
  patient: Patient
  doctor: Doctor
  hospital: Hospital
  procedures: any[]
  cids: any[]
  tussList: any[]
  opmes: any[]
  documents_attached: any[]
  use_opme: boolean
  observations: string
  healthInsurance: PatientHealthInsurance
  surgery_center: SurgeryCenter
  createdAt: string
  patientHospitalized: boolean
  expectedDate: string
  insuranceCardValidity: string
  patientName?: string
  bloodTransfusion?: boolean
  canSee?: {
    hasExams?: boolean
    hasMedicalReport?: boolean
  }
  cooperative_name?: string
  anesthesiology?: {
    anesthesiology_id: number
    name: string
  }[]
  locale_hospitalization?: string
  bullish_forecast_date?: string
  anesthesiologies?: {
    anesthesiology_id?: number
    name?: string
  }[]
  report_recommendation?: string
  documents?: Document[]
  oncology_or_research?: boolean
  opme_denied_reason?: string
}

const validationSchema = {
  surgery_center: yup.mixed().required(),
  allergy: yup.mixed().required(),
  hospitalizationMode: yup.string().required(),
  hospitalizationType: yup.string().required(),
  procedures: yup.array().min(1),
  cids: yup.array().min(1),
  clinicalHistory: yup.string().required(),
  extraBlood: yup.boolean().required(),
  expectedDate: yup
    .date()
    .typeError('Data Inválida')
    .format('YYYY-MM-DD', true)
    .nullable()
    .test(
      'greater_then_today',
      'A data informada não pode ser menor que a atual.',
      (value) => {
        if (value) {
          const date = moment(value, 'YYYY-MM-DD').startOf('day')
          const now = moment().startOf('day')
          if (date.isValid()) {
            return date >= now
          }
        }
        return true
      }
    ),
  use_opme: yup.boolean().required(),
  opmes: yup.array().when('use_opme', {
    is: (value: boolean) => value,
    then: yup.array().min(1)
  }),
  providers: yup.array().when('opmes', {
    is: (value: []) => !!value.length,
    then: yup.array().test('opme_size', (value) => {
      return !!value?.some((val) => val)
    })
  }),
  technique: yup.string().required()
}

const opmeSchema = yup.object().shape({
  ...validationSchema,

  requestedDailies: yup.number().when('hospitalizationMode', {
    is: (value: string) => value !== 'ambulatory',
    then: yup.number().min(0).required()
  })
})

const secretarySchema = yup.object().shape({
  ...validationSchema,
  bloodTransfusion: yup.boolean().required()
})

const formatOpme = (opmes: any[]) => {
  return opmes.map((item: any) => ({
    description: item.description,
    quantity: Number(item.quantity)
  }))
}

const formatProcedures = (procedures: any[]) => {
  return procedures.map((item) => ({
    isMain: !!item.principalProcedure,
    code: item.tuss_id,
    description: item.description,
    doctor_name: item.doctor,
    quantity: Number(item.quantity),
    procedure_code: item.tuss_id,
    surgery_id: item.surgery_id,
    pro_fat_id: item.pro_fat_id
  }))
}
